import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  Slider,
  FormControl,
  Select,
  MenuItem,
  Button,
  Stack,
  Grow,
  CircularProgress,
} from '@mui/material';

// Updated RBL theme colors
const theme = createTheme({
  typography: {
    h5: { fontSize: '1.1rem' },
    h6: { fontSize: '1rem' },
    body1: { fontSize: '0.9rem' },
    body2: { fontSize: '0.85rem' },
    subtitle2: { fontSize: '0.85rem' }
  },
  palette: {
    primary: {
      main: '#005DAA',
      light: '#FF8E21', // Light orange for completed sections
    },
    secondary: {
      main: '#DA291C',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
      input: '#f5f5f5',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          boxShadow: 'none',
          border: 'none',
          minHeight: '30px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          backgroundColor: '#f8f9fa',
          height: '30px',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
          '&.Mui-focused': {
            backgroundColor: '#f8f9fa',
          },
          '& .MuiOutlinedInput-input': {
            height: '30px',
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.9rem'
          }
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: '4px',
          backgroundColor: '#f8f9fa',
          height: '30px',
          padding: '0 14px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          padding: '12px 24px',
          textTransform: 'none',
          fontWeight: 600,
          marginLeft: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(14px, -12px) scale(0.75)',
          '&.Mui-focused, &.MuiFormLabel-filled': {
            transform: 'translate(14px, -12px) scale(0.75)'
          }
        }
      }
    }
  },
});

const prioritizationItems = [
  'Hours of Work',
  'Compensation',
  'Benefits',
  'Training and development',
  'Upward Mobility',
  'Work-life balance',
  'Facilities',
  'Corporate Culture',
  'Relationship with my manager',
  'Company brand',
  'Equity',
];

const payMixElements = [
  { name: 'Base pay', color: '#005DAA' },
  { name: 'Guaranteed cash allowances', color: '#FDB515' },
  { name: 'Conditional Pay', color: '#00A3E0' },
  { name: 'Incentives and commission', color: '#7AB800' },
];

// Loading dots animation component
const LoadingDots = () => {
  return (
    <Box sx={{
      display: 'flex',
      gap: 1,
      alignItems: 'center',
      justifyContent: 'center',
      my: 2,
      height: '40px' // Fixed height to prevent layout shift
    }}>
      <CircularProgress size={8} sx={{
        color: theme.palette.primary.main,
        animation: 'pulse 0.8s ease-in-out infinite'
      }} />
      <CircularProgress size={8} sx={{
        color: theme.palette.primary.main,
        animation: 'pulse 0.8s ease-in-out infinite',
        animationDelay: '0.2s'
      }} />
      <CircularProgress size={8} sx={{
        color: theme.palette.primary.main,
        animation: 'pulse 0.8s ease-in-out infinite',
        animationDelay: '0.4s'
      }} />
    </Box>
  );
};

// Replace NotepadIcon with AnimatedIcon
const AnimatedIcon = () => (
  <Box
    component="img"
    src="https://i.imgur.com/eFrop0a.png"
    alt="animated icon"
    sx={{
      width: '24px',
      height: '24px',
      animation: 'float 2s ease-in-out infinite',
      '@keyframes float': {
        '0%': {
          transform: 'translateY(0px)'
        },
        '50%': {
          transform: 'translateY(-5px)'
        },
        '100%': {
          transform: 'translateY(0px)'
        }
      }
    }}
  />
);

function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [introStep, setIntroStep] = useState(0);
  const [hasConsented, setHasConsented] = useState(false);
  const [showIntroLoading, setShowIntroLoading] = useState(false);
  const surveyContentRef = React.useRef(null);

  // Original states
  const [personalInfo, setPersonalInfo] = useState({
    age: '',
    gender: '',
    workTenure: '',
  });

  // State for prioritization
  const [items, setItems] = useState(prioritizationItems);

  // State for pay mix
  const [payMix, setPayMix] = useState({
    'Base pay': 0,
    'Guaranteed cash allowances': 0,
    'Conditional Pay': 0,
    'Incentives and commission': 0,
  });

  // State for controlling the flow
  const [currentStep, setCurrentStep] = useState('age');
  const [showPrioritization, setShowPrioritization] = useState(false);
  const [showPayMix, setShowPayMix] = useState(false);

  // Add completed sections tracking
  const [completedSections, setCompletedSections] = useState({
    age: false,
    gender: false,
    workTenure: false,
    prioritization: false,
  });

  // Add animation delay state
  const [showLoading, setShowLoading] = useState(false);
  const [animationTimeout, setAnimationTimeout] = useState(null);

  // Common styles for input fields
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      backgroundColor: '#f8f9fa',
      height: '30px',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
      '&.Mui-focused': {
        backgroundColor: '#f8f9fa',
      },
      '& .MuiOutlinedInput-input': {
        height: '30px',
        padding: '0 14px',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.9rem'
      }
    },
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, -12px) scale(0.75)',
      '&.Mui-focused, &.MuiFormLabel-filled': {
        transform: 'translate(14px, -12px) scale(0.75)'
      }
    }
  };

  // Add state for form submission
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Add state for controlling success message animation
  const [showFirstMessage, setShowFirstMessage] = useState(false);
  const [showFirstLoading, setShowFirstLoading] = useState(false);
  const [showSecondMessage, setShowSecondMessage] = useState(false);
  const [showSecondLoading, setShowSecondLoading] = useState(false);

  // Auto-progress intro messages
  useEffect(() => {
    if (showIntro && introStep < 3) {
      setShowIntroLoading(true);
      const timer = setTimeout(() => {
        setShowIntroLoading(false);
        setIntroStep(prev => prev + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showIntro, introStep]);

  // Auto-scroll effect with offset
  useEffect(() => {
    if (surveyContentRef.current && !showIntro) {
      const yOffset = -20; // Smaller offset for better heading visibility
      setTimeout(() => {
        const element = surveyContentRef.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }, 100); // Small delay to ensure elements are rendered
    }
  }, [currentStep, showIntro]);

  // Add specific scroll for pay mix section
  useEffect(() => {
    if (currentStep === 'payMix') {
      setTimeout(() => {
        const payMixSection = document.querySelector('[data-section="payMix"]');
        if (payMixSection) {
          const yOffset = -20;
          const y = payMixSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [currentStep]);

  const handleConsent = (consent) => {
    if (consent) {
      setHasConsented(true);
      setShowIntro(false);
      setCurrentStep('age');
    } else {
      window.close();
    }
  };

  // Handle personal info changes
  const handlePersonalInfoChange = (field) => (event) => {
    setPersonalInfo({
      ...personalInfo,
      [field]: event.target.value,
    });
  };

  // Modified handleNextStep with animation
  const handleNextStep = () => {
    setShowLoading(true);

    // Clear any existing timeout
    if (animationTimeout) {
      clearTimeout(animationTimeout);
    }

    // Hide current section immediately
    switch (currentStep) {
      case 'age':
        if (personalInfo.age) {
          setCompletedSections({ ...completedSections, age: true });
        }
        break;
      case 'gender':
        if (personalInfo.gender) {
          setCompletedSections({ ...completedSections, gender: true });
        }
        break;
      case 'workTenure':
        if (personalInfo.workTenure) {
          setCompletedSections({ ...completedSections, workTenure: true });
        }
        break;
      case 'prioritization':
        setCompletedSections({ ...completedSections, prioritization: true });
        break;
      default:
        break;
    }

    // Set new timeout for animation
    const timeout = setTimeout(() => {
      setShowLoading(false);
      switch (currentStep) {
        case 'age':
          if (personalInfo.age) setCurrentStep('gender');
          break;
        case 'gender':
          if (personalInfo.gender) setCurrentStep('workTenure');
          break;
        case 'workTenure':
          if (personalInfo.workTenure) setCurrentStep('prioritization');
          break;
        case 'prioritization':
          setCurrentStep('payMix');
          break;
        default:
          break;
      }
    }, 1500); // 1.5 seconds delay

    setAnimationTimeout(timeout);
  };

  // Update the handleDragEnd function
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const newItems = Array.from(items);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destinationIndex, 0, removed);

    setItems(newItems);
  };

  // Handle pay mix changes
  const handlePayMixChange = (element) => (event, newValue) => {
    const currentTotal = Object.entries(payMix)
      .filter(([key]) => key !== element)
      .reduce((sum, [, value]) => sum + value, 0);

    if (currentTotal + newValue <= 100) {
      setPayMix({
        ...payMix,
        [element]: newValue,
      });
    }
  };

  // Update the handleSubmit function to format data as text
  const handleSubmit = async () => {
    const formData = {
      personalInfo,
      prioritization: items,
      payMix,
    };

    setFormSubmitted(true);

    setTimeout(() => {
      setShowFirstMessage(true);
      setShowSecondLoading(true);

      setTimeout(() => {
        setShowSecondLoading(false);
        setShowSecondMessage(true);
      }, 1500);
    }, 1500);

    try {
      const response = await fetch('/rbl-survey/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          prioritization: items,
          payMix: Object.values(payMix)
        }),
      });

      if (!response.ok) {
        console.error('Error submitting survey');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Box sx={{
          my: 4,
          minHeight: '100vh',
          bgcolor: '#ffffff',
          borderRadius: 4,
          p: 3,
          overflowX: 'hidden',
          '& .MuiPaper-root': {
            minHeight: '30px',
          },
          '& .MuiButton-root': {
            marginLeft: 0,
          },
          '& .completed-section': {
            padding: '4px 16px',
            backgroundColor: theme.palette.primary.light,
            borderRadius: '8px',
            height: '30px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
            width: 'fit-content'
          }
        }}>
          {/* RBL Logo */}
          <Box sx={{
            textAlign: 'center',
            mb: 4,
            width: '100%',
            backgroundColor: 'transparent',
            '@keyframes spinIn': {
              '0%': {
                transform: 'rotate(0deg) scale(0.5)',
                opacity: 0
              },
              '100%': {
                transform: 'rotate(360deg) scale(1)',
                opacity: 1
              }
            }
          }}>
            <img
              src="https://i.imgur.com/dA2MaFC.png"
              alt="RBL Logo"
              crossOrigin="anonymous"
              loading="eager"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                backgroundColor: 'transparent',
                animation: 'spinIn 1.5s ease-out forwards'
              }}
              onError={(e) => {
                console.error('Image failed to load:', e);
                const currentSrc = e.target.src;
                e.target.src = '';
                setTimeout(() => {
                  e.target.src = currentSrc;
                }, 1000);
              }}
            />
          </Box>

          {/* Intro Messages - Always visible */}
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {introStep >= 0 && (
              <Grow in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AnimatedIcon />
                  <Paper sx={{
                    p: 1.5,
                    textAlign: 'left',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    width: 'auto',
                    minHeight: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}>
                    <Typography variant="h6" sx={{ m: 0, color: '#000000' }}>
                      Thank you for participating in our survey. Your feedback is invaluable in helping us understand what matters most to you in your work experience and compensation structure.
                    </Typography>
                  </Paper>
                </Box>
              </Grow>
            )}

            {introStep >= 1 && (
              <Grow in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AnimatedIcon />
                  <Paper sx={{
                    p: 1.5,
                    textAlign: 'left',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    width: 'auto',
                    minHeight: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}>
                    <Typography variant="h6" sx={{ m: 0, color: '#000000' }}>
                      This interactive survey is designed to be quick, intuitive, and engaging. You'll be able to rank key workplace elements by sliding the most important factors to the top. Additionally, you'll have the opportunity to customize your ideal pay mix by adjusting percentage allocations across different compensation components.
                    </Typography>
                  </Paper>
                </Box>
              </Grow>
            )}

            {introStep >= 2 && (
              <Grow in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AnimatedIcon />
                  <Paper sx={{
                    p: 1.5,
                    textAlign: 'left',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    width: 'auto',
                    minHeight: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}>
                    <Typography variant="h6" sx={{ m: 0, color: '#000000' }}>
                      Your responses will remain confidential, and the insights gathered will help shape a better workplace experience. Let's get started!
                    </Typography>
                  </Paper>
                </Box>
              </Grow>
            )}

            {showIntroLoading && <LoadingDots />}

            {introStep >= 3 && !hasConsented && (
              <Grow in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AnimatedIcon />
                  <Paper sx={{
                    p: 1.5,
                    textAlign: 'left',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    width: 'auto', // Change to auto to fit content
                  }}>
                    <Typography variant="h6" gutterBottom sx={{ m: 0, mb: 1, color: '#000000' }}>
                      DISCLAIMER:
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      By using our bot, you consent to how we handle the information.
                    </Typography>
                    <Stack direction="column" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleConsent(true)}
                        sx={{ width: '200px' }}
                      >
                        I Consent
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleConsent(false)}
                        sx={{ width: '200px' }}
                      >
                        I Don't Consent
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              </Grow>
            )}
          </Stack>

          {/* Survey Content */}
          {hasConsented && (
            <Box ref={surveyContentRef}>
              <Stack spacing={2}>
                {/* Name Section */}
                {(currentStep === 'age' || completedSections.age) && (
                  <Grow in timeout={500}>
                    <Paper sx={{ p: completedSections.age ? 0 : 2, bgcolor: 'background.paper' }}>
                      {completedSections.age ? (
                        <Box className="completed-section" sx={{
                          height: 'auto !important',
                          padding: '8px 16px',
                          maxHeight: 'none',
                          width: 'max-content',
                          display: 'inline-flex',
                          marginLeft: 'auto',
                          backgroundColor: theme.palette.primary.light
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.1rem', whiteSpace: 'nowrap' }}>{personalInfo.age}</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="h5" gutterBottom color="primary">
                            Kindly select your age:
                          </Typography>
                          <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <AnimatedIcon />

                           <FormControl fullWidth sx={{ mb: 2 }}>
                              <Select
                                value={personalInfo.age}
                                onChange={handlePersonalInfoChange('age')}
                                displayEmpty
                                variant="outlined"
                                sx={{
                                  '& .MuiSelect-select': {
                                    borderRadius: '4px',
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>Select your age group</MenuItem>
                                <MenuItem value="18-24">18-24</MenuItem>
                                <MenuItem value="25-34">25-34</MenuItem>
                                <MenuItem value="35-44">35-44</MenuItem>
                                <MenuItem value="45-54">45-54</MenuItem>
                                <MenuItem value="55-64">55-64</MenuItem>
                                <MenuItem value="65+">65+</MenuItem>
                              </Select>
                            </FormControl>

                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleNextStep}
                              disabled={!personalInfo.age}
                              sx={{ width: '200px' }}
                            >
                              Next
                            </Button>
                          </Box>
                        </>
                      )}
                    </Paper>
                  </Grow>
                )}

                {/* Loading animation after age */}
                {showLoading && currentStep === 'age' && <LoadingDots />}

                {/* Gender Section */}
                {(currentStep === 'gender' || completedSections.gender) && (
                  <Grow in timeout={500}>
                    <Paper sx={{ p: completedSections.gender ? 0 : 2, bgcolor: 'background.paper' }}>
                      {completedSections.gender ? (
                        <Box className="completed-section" sx={{
                          height: 'auto !important',
                          padding: '8px 16px',
                          maxHeight: 'none',
                          width: 'max-content',
                          display: 'inline-flex',
                          marginLeft: 'auto',
                          backgroundColor: theme.palette.primary.light
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.1rem', whiteSpace: 'nowrap' }}>{personalInfo.gender}</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="h5" gutterBottom color="primary">
                            Please select your gender:
                          </Typography>
                          <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AnimatedIcon />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <Select
                                value={personalInfo.gender}
                                onChange={handlePersonalInfoChange('gender')}
                                displayEmpty
                                variant="outlined"
                                sx={{
                                  '& .MuiSelect-select': {
                                    borderRadius: '4px',
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>Select your gender</MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleNextStep}
                              disabled={!personalInfo.gender}
                              sx={{ width: '200px' }}
                            >
                              Next
                            </Button>
                          </Box>
                        </>
                      )}
                    </Paper>
                  </Grow>
                )}

                {/* Loading animation after gender */}
                {showLoading && currentStep === 'gender' && <LoadingDots />}

                {/* Work Tenure Section */}
                {(currentStep === 'workTenure' || completedSections.workTenure) && (
                  <Grow in timeout={500}>
                    <Paper sx={{ p: completedSections.workTenure ? 0 : 2, bgcolor: 'background.paper' }}>
                      {completedSections.workTenure ? (
                        <Box className="completed-section" sx={{
                          height: 'auto !important',
                          padding: '8px 16px',
                          maxHeight: 'none',
                          width: 'max-content',
                          display: 'inline-flex',
                          marginLeft: 'auto',
                          backgroundColor: theme.palette.primary.light
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.1rem', whiteSpace: 'nowrap' }}>{personalInfo.workTenure} years</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="h5" gutterBottom color="primary">
                            How many years have you been working?
                          </Typography>
                          <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <AnimatedIcon />
                            <TextField
                              fullWidth
                              placeholder="Enter years of work experience"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{ shrink: false }}
                              value={personalInfo.workTenure}
                              onChange={handlePersonalInfoChange('workTenure')}
                              sx={inputStyles}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleNextStep}
                              disabled={!personalInfo.workTenure}
                              sx={{ width: '200px' }}
                            >
                              Next
                            </Button>
                          </Box>
                        </>
                      )}
                    </Paper>
                  </Grow>
                )}

                {/* Loading animation after work tenure */}
                {showLoading && currentStep === 'workTenure' && <LoadingDots />}

                {/* Prioritization Section */}
                {(currentStep === 'prioritization' || completedSections.prioritization) && (
                  <Grow in timeout={500}>
                    <Paper sx={{ p: completedSections.prioritization ? 0 : 2, bgcolor: 'background.paper' }}>
                      {completedSections.prioritization ? (
                        <Box className="completed-section" sx={{
                          height: 'auto !important',
                          padding: '8px 16px',
                          maxHeight: 'none',
                          width: 'max-content',
                          display: 'inline-flex',
                          marginLeft: 'auto',
                          backgroundColor: theme.palette.primary.light
                        }}>
                          {items.map((item, index) => (
                            <Typography key={item} variant="h6" sx={{
                              fontWeight: 500,
                              mt: index === 0 ? 0 : 0.25,
                              color: '#ffffff',
                              whiteSpace: 'nowrap',
                              fontSize: '1.1rem'
                            }}>
                              {index + 1}. {item}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <AnimatedIcon />
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="h5" gutterBottom color="primary">
                                Prioritization
                              </Typography>
                              <Typography variant="body1" sx={{ mb: 3 }}>
                                Drag and drop these elements in order of importance (1 being the most important and 11 being the least important)
                              </Typography>

                              <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="prioritization-list">
                                  {(provided) => (
                                    <Box
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      sx={{
                                        minHeight: 500,
                                        bgcolor: '#f8f9fa',
                                        borderRadius: 2,
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                      }}
                                    >
                                      {items.map((item, index) => (
                                        <Draggable
                                          key={item}
                                          draggableId={item}
                                          index={index}
                                        >
                                          {(provided, snapshot) => (
                                            <Paper
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              elevation={snapshot.isDragging ? 8 : 1}
                                              sx={{
                                                p: 1,
                                                minHeight: '35px',
                                                backgroundColor: snapshot.isDragging
                                                  ? theme.palette.primary.light
                                                  : '#ffffff',
                                                border: `2px solid ${snapshot.isDragging
                                                  ? theme.palette.primary.main
                                                  : 'transparent'}`,
                                                cursor: 'grab',
                                                '&:hover': {
                                                  backgroundColor: theme.palette.primary.light,
                                                  transform: 'translateX(5px)',
                                                },
                                                '&:active': {
                                                  cursor: 'grabbing',
                                                  transform: 'scale(1.02)',
                                                },
                                                display: 'flex',
                                                alignItems: 'center',
                                                userSelect: 'none',
                                                position: 'relative',
                                                zIndex: snapshot.isDragging ? 9999 : 'auto'
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontWeight: 600,
                                                  color: theme.palette.primary.main,
                                                  width: '30px',
                                                  flexShrink: 0
                                                }}
                                                variant="h6"
                                              >
                                                {index + 1}.
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontWeight: 500,
                                                  color: snapshot.isDragging
                                                    ? theme.palette.primary.main
                                                    : 'text.primary',
                                                  flex: 1
                                                }}
                                                variant="h6"
                                              >
                                                {item}
                                              </Typography>
                                            </Paper>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </Box>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleNextStep}
                              sx={{ width: '200px' }}
                            >
                              Next
                            </Button>
                          </Box>
                        </>
                      )}
                    </Paper>
                  </Grow>
                )}

                {/* Loading animation after prioritization */}
                {showLoading && currentStep === 'prioritization' && <LoadingDots />}

                {/* Pay Mix Section */}
                {currentStep === 'payMix' && (
                  <Grow in timeout={500}>
                    <Paper sx={{ p: 3 }} data-section="payMix">
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <AnimatedIcon />
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="h5" gutterBottom color="primary">
                            Pay Mix
                          </Typography>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Describe your ideal mix in terms of the contribution of the enclosed elements to your Total Compensation (Total must equal 100%)
                          </Typography>

                          {payMixElements.map((element) => (
                            <Box key={element.name} sx={{ mb: 2 }}>
                              <Typography gutterBottom>
                                {element.name}: {payMix[element.name]}%
                              </Typography>
                              <Slider
                                value={payMix[element.name]}
                                onChange={handlePayMixChange(element.name)}
                                sx={{
                                  '& .MuiSlider-thumb': {
                                    backgroundColor: element.color,
                                  },
                                  '& .MuiSlider-track': {
                                    backgroundColor: element.color,
                                  },
                                }}
                              />
                            </Box>
                          ))}

                          <Typography variant="h6" sx={{ mt: 2 }}>
                            Total: {Object.values(payMix).reduce((a, b) => a + b, 0)}%
                          </Typography>

                          {!formSubmitted && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                sx={{ width: '200px', mt: 2 }}
                              >
                                Submit Survey
                              </Button>
                            </Box>
                          )}
                          {formSubmitted && !showFirstMessage && <LoadingDots />}
                        </Box>
                      </Box>
                    </Paper>
                  </Grow>
                )}

                {/* Update success message section */}
                {formSubmitted && (
                  <Stack spacing={0.5}>
                    {showFirstMessage && (
                      <Grow in timeout={500}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <AnimatedIcon />
                          <Paper sx={{
                            p: 1.5,
                            textAlign: 'left',
                            borderRadius: '16px',
                            backgroundColor: '#f8f9fa',
                            width: '100%',
                            minHeight: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            mb: 0.5
                          }}>
                            <Typography variant="h6" sx={{ m: 0, color: '#000000' }}>
                              Thank You for taking part in the RBL Prioritization survey
                            </Typography>
                          </Paper>
                        </Box>
                      </Grow>
                    )}

                    {showSecondLoading && !showSecondMessage && <LoadingDots />}

                    {showSecondMessage && (
                      <Grow in timeout={500}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <AnimatedIcon />
                          <Paper sx={{
                            p: 1.5,
                            textAlign: 'left',
                            borderRadius: '16px',
                            backgroundColor: '#f8f9fa',
                            width: '100%',
                            minHeight: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            mb: 0.5
                          }}>
                            <Typography variant="h6" sx={{ m: 0, color: '#000000' }} ref={el => {
                              if (el) {
                                setTimeout(() => {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }, 100);
                              }
                            }}>
                              Your survey responses were successfully submitted
                            </Typography>
                          </Paper>
                        </Box>
                      </Grow>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
